import { Link } from "react-router-dom";
import { ArrowRight, Shirt, Sparkles } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";

const ITEMS = [
  {
    id: "grey-set",
    name: "Seamless Long Sleeve Set",
    color: "Heather Grey",
    img: "/merch-grey.jpeg",
    testid: "merch-card-grey-set",
  },
  {
    id: "black-set",
    name: "Cropped Sweatshirt & Legging Set",
    color: "Jet Black",
    img: "/merch-black.jpeg",
    testid: "merch-card-black-set",
  },
];

const Merch = () => (
  <main data-testid="merch-page" className="pt-28">
    <section className="mx-auto max-w-7xl px-6 pb-14 pt-10 text-center">
      <MaskedLine delay={0.1}>
        <span className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-sage/15 px-4 py-1.5 font-display text-[11px] font-bold tracking-[0.25em] text-forest">
          <Shirt className="h-3.5 w-3.5 text-coral" /> FITBUNNYCAT MERCH
        </span>
      </MaskedLine>
      <h1 data-testid="merch-headline" className="mt-6 font-display text-4xl font-black uppercase tracking-tighter text-ink sm:text-5xl md:text-6xl">
        <MaskedLine delay={0.25}>The FitBunnyCat</MaskedLine>
        <MaskedLine delay={0.4}>
          <span className="text-coral">Collection ❤️</span>
        </MaskedLine>
      </h1>
      <FadeUp delay={0.55}>
        <p className="mx-auto mt-6 max-w-xl font-body text-base font-light leading-relaxed text-ink/70 md:text-lg">
          Take a first look at my exclusive line of cosmetics, fabric care solutions, and activewear — stylish, effective, and designed to move with you.
        </p>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-5xl px-6 pb-16">
      <FadeUp>
        <div
          data-testid="layover-card"
          className="overflow-hidden rounded-3xl border border-coral/30 bg-white shadow-lg shadow-coral/10 md:grid md:grid-cols-5"
        >
          <div className="flex flex-col md:col-span-2">
            <div className="relative min-h-[320px] flex-1 overflow-hidden">
              <img
                src="/layover-lifestyle.webp"
                alt="LAYOVER luxury laundry detergent bottle beside a folded flight attendant uniform"
                data-testid="layover-photo"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
              <span data-testid="layover-new-badge" className="absolute left-4 top-4 rounded-full bg-coral px-3 py-1 font-display text-[10px] font-bold tracking-[0.15em] text-bone shadow-md">
                NEW
              </span>
              <span data-testid="layover-bestseller-badge" className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-forest/90 px-5 py-1.5 font-display text-[11px] font-bold tracking-[0.2em] text-bone shadow-lg backdrop-blur">
                ⭐ BEST SELLER
              </span>
            </div>
            <div className="border-t border-forest/10 p-6 text-center">
              <a
                href="https://square.link/u/rWv9uYav"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="layover-buy-button"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-coral px-8 py-4 font-display text-xs font-bold tracking-[0.15em] text-bone shadow-lg shadow-coral/30 transition-all duration-300 hover:scale-[1.02] hover:bg-coraldark"
              >
                BUY NOW — FROM $29 ❤️
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <p className="mt-3 font-body text-xs font-light text-ink/45">Available 16oz &amp; 32oz — secure checkout by Square.</p>
            </div>
          </div>
          <div className="p-8 md:col-span-3 md:p-10">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-block rounded-full bg-forest px-4 py-1.5 font-display text-[11px] font-bold tracking-[0.15em] text-bone shadow-md">
                NOW AVAILABLE ✨
              </span>
              <span data-testid="layover-price" className="font-display text-lg font-extrabold tracking-tight text-forest">
                Available 16oz &amp; 32oz
              </span>
            </div>
            <h2 data-testid="layover-headline" className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-ink md:text-4xl">
              Layover <span className="text-coral">— luxury laundry fragrance</span>
            </h2>
            <div className="mt-5 space-y-4 font-body text-sm font-light leading-relaxed text-ink/70 md:text-base">
              <p className="font-display text-sm font-extrabold tracking-[0.1em] text-coral md:text-base">
                ✨ A SCENT PEOPLE CAN'T STOP TALKING ABOUT. ✨
              </p>
              <p>
                I created a rich, luxurious fragrance that transforms everyday laundry into an unforgettable experience. It's the kind of scent that makes people stop and ask, "What smells SO good?"
              </p>
              <p>
                I know because it happens to me all the time — people ask what I'm wearing, when the secret is actually my laundry. 🧺✨
              </p>
              <p>
                More than just clean clothes, it's a signature scent people remember. <span className="font-semibold text-ink">Rich. Luxurious. Irresistible.</span>
              </p>
              <p className="font-serif text-base italic text-forest md:text-lg">
                ✨ Laundry that leaves an impression.
              </p>
            </div>
          </div>
        </div>
      </FadeUp>

      <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2">
        {ITEMS.map((item, i) => (
          <FadeUp key={item.id} delay={0.1 * i}>
            <div
              data-testid={item.testid}
              className="group overflow-hidden rounded-3xl border border-forest/10 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="relative max-h-[520px] overflow-hidden">
                <img
                  src={item.img}
                  alt={`${item.name} in ${item.color}`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-coral px-4 py-1.5 font-display text-[11px] font-bold tracking-[0.15em] text-bone shadow-md">
                  SNEAK PEEK
                </span>
              </div>
              <div className="p-6 text-center">
                <h3 className="font-display text-xl font-extrabold tracking-tight text-ink">{item.name}</h3>
                <p className="mt-1 font-body text-sm font-medium text-coral">{item.color}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    <section className="grain relative overflow-hidden bg-forest px-6 py-24 text-center">
      <FadeUp>
        <Sparkles className="mx-auto h-10 w-10 text-coral" strokeWidth={1.5} />
        <h2 data-testid="merch-coming-soon" className="mt-6 font-display text-4xl font-black uppercase tracking-tight text-bone md:text-5xl">
          New styles coming soon!
        </h2>
        <p className="mx-auto mt-4 max-w-md font-body text-base font-light leading-relaxed text-bone/75 md:text-lg">
          This is just the beginning — more colors and styles are on the way. Want to be the first to know when they drop? Send me a quick email.
        </p>
        <Link
          to="/get-started#contact"
          data-testid="merch-notify-button"
          className="group mt-10 inline-flex items-center gap-2 rounded-full bg-coral px-10 py-5 font-display text-sm font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coraldark"
        >
          NOTIFY ME WHEN IT DROPS ❤️
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
      <FadeUp delay={0.15} className="mt-12">
        <Link
          to="/shop"
          data-testid="merch-shop-link"
          className="font-display text-xs font-bold tracking-[0.25em] text-bone/70 underline decoration-coral/60 underline-offset-8 transition-colors hover:text-coral"
        >
          IN THE MEANTIME, SHOP MY FAVORITE PRODUCTS →
        </Link>
      </FadeUp>
    </section>
  </main>
);

export default Merch;
